import type { Attachment } from "./entities";

export const ATTACHMENT_MAX_BYTES = 32_000_000;
export const ATTACHMENT_MAX_PER_SELECTION = 10;
export const ATTACHMENT_MAX_ACTIVE = 20;
export const ATTACHMENT_MAX_TOTAL_BYTES = 200 * 1024 * 1024;
export const ATTACHMENT_BUCKET = "task-attachments";
export const ATTACHMENT_UNDO_MS = 10_000;

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif", "avif"] as const;
const MARKDOWN_EXTENSIONS = ["md", "markdown"] as const;
const DOCUMENT_EXTENSIONS = ["txt", "csv", "pdf", "rtf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods", "odp"] as const;

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"];
const MARKDOWN_TYPES = ["text/markdown", "text/x-markdown", "text/plain"];
const DOCUMENT_TYPES = ["text/plain", "text/csv", "application/pdf", "application/rtf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation", "application/vnd.oasis.opendocument.text", "application/vnd.oasis.opendocument.spreadsheet", "application/vnd.oasis.opendocument.presentation"];

export type AttachmentKind = "image" | "markdown" | "document";

export function attachmentExtension(name: string) {
  const index = name.lastIndexOf(".");
  return index >= 0 ? name.slice(index + 1).toLocaleLowerCase("en-US") : "";
}

export function attachmentKind(attachment: Pick<Attachment, "originalName" | "mediaType">): AttachmentKind {
  const extension = attachmentExtension(attachment.originalName);
  if ((IMAGE_EXTENSIONS as readonly string[]).includes(extension) && IMAGE_TYPES.includes(attachment.mediaType)) return "image";
  if ((MARKDOWN_EXTENSIONS as readonly string[]).includes(extension)) return "markdown";
  return "document";
}

export function isPreviewableAttachment(attachment: Pick<Attachment, "originalName" | "mediaType" | "state">) {
  return attachment.state === "ready" && attachmentKind(attachment) !== "document";
}

export function supportedAttachmentAccept() {
  return [...IMAGE_EXTENSIONS, ...MARKDOWN_EXTENSIONS, ...DOCUMENT_EXTENSIONS].map((extension) => `.${extension}`).join(",");
}

export function attachmentTypeLabel(attachment: Pick<Attachment, "originalName" | "mediaType">) {
  const extension = attachmentExtension(attachment.originalName);
  return extension ? extension.toLocaleUpperCase("en-US") : attachment.mediaType || "File";
}

export function formatAttachmentBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateAttachmentFile(file: { name: string; type: string; size: number }): string | null {
  const extension = attachmentExtension(file.name);
  const allowed = [...IMAGE_EXTENSIONS, ...MARKDOWN_EXTENSIONS, ...DOCUMENT_EXTENSIONS] as readonly string[];
  if (!extension || !allowed.includes(extension)) return "This file type is not supported.";
  if (file.size <= 0) return "The file is empty.";
  if (file.size > ATTACHMENT_MAX_BYTES) return `This file is larger than ${formatAttachmentBytes(ATTACHMENT_MAX_BYTES)}.`;
  const mediaType = file.type.toLocaleLowerCase("en-US");
  if (mediaType) {
    const declared = [...IMAGE_TYPES, ...MARKDOWN_TYPES, ...DOCUMENT_TYPES];
    if (!declared.includes(mediaType)) return "The file type reported by the browser is not supported.";
    const expectedImage = (IMAGE_EXTENSIONS as readonly string[]).includes(extension);
    if (expectedImage && !IMAGE_TYPES.includes(mediaType)) return "The image type does not match its extension.";
  }
  return null;
}

export function validateAttachmentContent(name: string, mediaType: string, bytes: Uint8Array): string | null {
  const extension = attachmentExtension(name);
  const startsWith = (signature: number[]) => signature.every((value, index) => bytes[index] === value);
  if ((IMAGE_EXTENSIONS as readonly string[]).includes(extension)) {
    const valid = extension === "png" ? startsWith([0x89, 0x50, 0x4e, 0x47])
      : (extension === "jpg" || extension === "jpeg") ? startsWith([0xff, 0xd8, 0xff])
        : extension === "gif" ? startsWith([0x47, 0x49, 0x46, 0x38])
          : extension === "webp" ? startsWith([0x52, 0x49, 0x46, 0x46]) && new TextDecoder().decode(bytes.slice(8, 12)) === "WEBP"
            : startsWith([0x66, 0x74, 0x79, 0x70]) || new TextDecoder().decode(bytes.slice(4, 8)) === "ftyp";
    if (!valid) return "The image content does not match its file type.";
    return null;
  }
  if ((MARKDOWN_EXTENSIONS as readonly string[]).includes(extension)) {
    try {
      new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
      return "Markdown files must contain valid UTF-8 text.";
    }
  }
  if (mediaType === "text/csv" || mediaType === "text/plain") {
    try {
      new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
      return "Text files must contain valid UTF-8 text.";
    }
  }
  return null;
}
